import {
  SettingsLogoGrid,
  SettingsLogoPlate,
  SettingsLogoTile,
  SettingsSearchField,
} from '@affine/component/setting-components';
import { useI18n } from '@affine/i18n';
import { LockIcon } from '@blocksuite/icons/rc';
import { type ReactNode, useMemo, useState } from 'react';

import * as styles from './settings-service-picker.css';

/** A third-party service as the pickers show it. */
export interface SettingsServiceChoice {
  slug: string;
  name: string;
  icon: string;
}

/**
 * Step one of adding a credential or a connection: every service with its
 * logo, narrowed by a search.
 */
export function SettingsServiceGrid<T extends SettingsServiceChoice>({
  choices,
  isLoading,
  meta,
  extra,
  testIdPrefix,
  onPick,
}: {
  choices: readonly T[];
  isLoading: boolean;
  /** Short remark below a service's name, e.g. the kind of sign-in. */
  meta?: (service: T) => ReactNode;
  /** Tiles after the services, e.g. a custom service. */
  extra?: ReactNode;
  /** Prefix of the tiles' and the search's test ids. */
  testIdPrefix: string;
  onPick: (service: T) => void;
}) {
  const t = useI18n();
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      choices.filter(
        service =>
          !needle ||
          service.name.toLowerCase().includes(needle) ||
          service.slug.toLowerCase().includes(needle)
      ),
    [choices, needle]
  );

  return (
    <div className={styles.serviceStep}>
      <SettingsSearchField
        autoFocus
        value={query}
        onChange={setQuery}
        placeholder={t[
          'com.streamlain.settings.workspace.credentials.form.search-service'
        ]()}
        data-testid={`${testIdPrefix}-search`}
      />
      {isLoading ? (
        <p className={styles.serviceNote} role="status">
          {t[
            'com.streamlain.settings.workspace.credentials.form.loading-services'
          ]()}
        </p>
      ) : null}
      {!isLoading && needle && matches.length === 0 ? (
        <p className={styles.serviceNote} role="status">
          {t[
            'com.streamlain.settings.workspace.credentials.form.no-service-match'
          ]({ query: query.trim() })}
        </p>
      ) : null}
      <SettingsLogoGrid
        aria-label={t[
          'com.streamlain.settings.workspace.credentials.detail.service'
        ]()}
      >
        {matches.map(service => (
          <SettingsLogoTile
            key={service.slug}
            logo={<SettingsServiceLogo service={service} />}
            name={service.name}
            meta={meta?.(service)}
            onClick={() => onPick(service)}
            data-testid={`${testIdPrefix}-${service.slug}`}
          />
        ))}
        {extra}
      </SettingsLogoGrid>
    </div>
  );
}

/** The logo plate of a service; a service without one shows a lock. */
export function SettingsServiceLogo({
  service,
}: {
  service: Pick<SettingsServiceChoice, 'icon'> | null;
}) {
  return (
    <SettingsLogoPlate neutral={!service}>
      {service ? (
        <img className={styles.serviceLogoImage} src={service.icon} alt="" />
      ) : (
        <LockIcon className={styles.serviceLogoIcon} />
      )}
    </SettingsLogoPlate>
  );
}
